import { useMutation } from "@apollo/client/react";
import { Box, IconicButton, TruncatedText } from "@nulogy/components";
import DaysSelector from "components/common/DaysSelector";
import { GridBox6, StyledInput, StyledText, StyledTimePicker } from "components/common/EditableListStyledComponents";
import FieldRow from "components/common/FieldRow";
import Form from "components/controls/Form";
import { UpdateShift } from "hooks/apollo/shift/shift.gql";
import React, { useState } from "react";
import DateTime from "utils/DateTime";
import Time from "utils/Time";

function displayNameForDay(day) {
  const map = {
    friday: "Fri",
    monday: "Mon",
    saturday: "Sat",
    sunday: "Sun",
    thursday: "Thu",
    tuesday: "Tue",
    wednesday: "Wed",
  };

  return map[day];
}

export default function Shift({ shift, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [updateShift] = useMutation(UpdateShift, { onCompleted: () => setEditing(false) });

  const { id, name, startTime, endTime } = shift;
  const formattedStartTime = Time.to12Hour(startTime);
  const formattedEndTime = Time.to12Hour(endTime);
  const days = [];

  DateTime.DAYS_OF_WEEK.forEach((day) => {
    if (shift[day] === true) {
      days.push(day);
    }
  });

  const handleCancel = (onReset) => {
    onReset();
    setEditing(false);
  };

  const handleSave = (updatedShift) => {
    const updatedDays = {};
    DateTime.DAYS_OF_WEEK.forEach((day) => {
      updatedDays[day] = updatedShift.days.includes(day);
    });
    const updatedStartTime = Time.to24Hour(updatedShift.startTime);
    const updatedEndTime = Time.to24Hour(updatedShift.endTime);

    return updateShift({
      variables: {
        endTime: updatedEndTime,
        id: updatedShift.id,
        name: updatedShift.name,
        startTime: updatedStartTime,
        ...updatedDays,
      },
    });
  };

  return (
    <>
      {!editing && (
        <GridBox6 className={`spec-shift-${id}`} data-testid="shift">
          <StyledText className="spec-shift-name" data-testid="shift-name">
            <TruncatedText fullWidth>{name}</TruncatedText>
          </StyledText>
          <StyledText data-testid="shift-days">{days.map(displayNameForDay).join(", ")}</StyledText>
          <StyledText data-testid="shift-start-time">{formattedStartTime}</StyledText>
          <StyledText data-testid="shift-end-time">{formattedEndTime}</StyledText>
          <IconicButton icon="edit" onClick={() => setEditing(true)} type="button">
            Edit
          </IconicButton>
          <IconicButton icon="delete" onClick={() => onDelete(shift)} type="button">
            Delete
          </IconicButton>
        </GridBox6>
      )}
      {editing && (
        <Form
          initialValues={{
            days,
            endTime: formattedEndTime,
            id,
            name,
            startTime: formattedStartTime,
          }}
          onSave={handleSave}
        >
          {({ dirty, handleReset }) => (
            <FieldRow data-testid="shift-row-editing" marginBottom="none">
              <GridBox6>
                <StyledInput field="name" placeholder="Name" />
                <DaysSelector />
                <Box data-testid="start-time">
                  <StyledTimePicker className="spec-shift-start-time" field="startTime" placeholder="Start Time" />
                </Box>
                <Box data-testid="end-time">
                  <StyledTimePicker className="spec-shift-end-time" field="endTime" placeholder="End Time" />
                </Box>
                <Box paddingTop="x1">
                  <IconicButton disabled={!dirty} icon="check" type="submit">
                    Save
                  </IconicButton>
                </Box>
                <Box paddingTop="x1">
                  <IconicButton icon="close" onClick={() => handleCancel(handleReset)} type="button">
                    Cancel
                  </IconicButton>
                </Box>
              </GridBox6>
            </FieldRow>
          )}
        </Form>
      )}
    </>
  );
}
